import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { useInstanceId, useInstance } from '../contexts/InstanceContext';
import { useTranslation } from '../hooks/useTranslation.jsx';
import { useScreenNavigation } from '../../../../packages/shared/useScreenNavigation';
import { useScreenStateUpdates } from '../../../../packages/shared/useScreenStateUpdates';
import { DashboardDataProvider, useDashboardData } from './useDashboardData';
import { DASHBOARD_TILES, BOTTOM_ROW_KEYS, tileByKey } from './tileRegistry';
import TileFrame from './TileFrame';
import DetailOverlay from './DetailOverlay';
import MainViewHeader from '../components/MainViewHeader';
import { DASH_COLORS, DASH_FONTS, DASH_TYPE } from './dashboardTokens';

// Tilannekuva — the live workshop dashboard (specs/workshop-dashboard.md,
// #1152/#1156). A standalone ongoing-situation screen, not a flow module:
// tiles anyone with participant access can open, on the 86" room touch
// panel (ScreenClient wall view 'dashboard') and on their own device at
// /tilannekuva. Tile expansion is local to the device.
//
// Shell layout (decision 2026-10-19): phase menu in the top-left corner,
// schedule under it, the workshop-flow bar scrolling down the left edge;
// step progress alone on the top row; rag / cost / feed / chat on the
// bottom row. Tiles carry no titles — each must read on its own.

const Page = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${DASH_COLORS.bgGradient};
  color: ${DASH_COLORS.text};
  font-family: ${DASH_FONTS.ui};
  overflow: hidden;
`;

const Content = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  gap: clamp(8px, 1.2vmin, 32px);
  padding: clamp(8px, 1.2vmin, 32px);
  box-sizing: border-box;

  /* Personal devices: one scrollable column. The page route is in
     noScrollPages; this container scrolls instead. */
  @media (max-width: 900px) {
    flex-direction: column;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const LeftBar = styled.div`
  flex: 0 0 clamp(220px, 20vw, 640px);
  display: flex;
  flex-direction: column;
  gap: clamp(8px, 1.2vmin, 32px);
  min-height: 0;

  @media (max-width: 900px) {
    flex: 0 0 auto;
  }
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: clamp(8px, 1.2vmin, 32px);
  min-width: 0;
  min-height: 0;

  @media (max-width: 900px) {
    flex: 0 0 auto;
  }
`;

/* Clock and live/stale chip live in the standard header's right section
   (next to the user icon — the chat popup covered them lower down), so they
   size to the header bar, not the 8 m scale; the flow tile's countdown is
   the room-readable timer. */
const Clock = styled.span`
  font-family: ${DASH_FONTS.numeric};
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1;
  color: ${DASH_COLORS.text};
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
  font-size: 0.8rem;
  color: ${({ stale }) => (stale ? DASH_COLORS.warn : DASH_COLORS.textDim)};

  &::before {
    content: '';
    width: 0.7em;
    height: 0.7em;
    border-radius: 50%;
    background: ${({ stale }) => (stale ? DASH_COLORS.warn : DASH_COLORS.accent)};
  }
`;

const BottomRow = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: clamp(8px, 1.2vmin, 32px);
  min-height: 0;

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    & > * { min-height: 160px; }
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${DASH_TYPE.label};
  color: ${DASH_COLORS.textDim};
  text-align: center;
  padding: 24px;
`;

// Per-slot sizing on the big screen; phones stack with min-heights.
const FLOW_STYLE = { flex: 1, minHeight: 160 };
const PROGRESS_STYLE = { flex: 1, minHeight: 160 };

function useWallClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

function DashboardBody() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentInstance } = useInstance();
  const { data, error } = useDashboardData();
  const now = useWallClock();
  const [openTileKey, setOpenTileKey] = useState(null);

  const openTile = DASHBOARD_TILES.find((tile) => tile.key === openTileKey) || null;
  const clock = now.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
  const stale = Boolean(error);

  // Keep the current params (?kid …) and let the target route add its own
  const goTo = (route) => {
    const [path, routeQuery] = route.split('?');
    const params = new URLSearchParams(window.location.search);
    new URLSearchParams(routeQuery || '').forEach((value, key) => params.set(key, value));
    navigate(`${path}?${params.toString()}`);
  };

  const renderTile = (key, style) => {
    const { labelKey, dataKey, Tile, route } = tileByKey(key);
    // A routed tile (the RAG map) opens the existing screen, params intact;
    // a live module whose manifest names an open route (dashboard.openRoute,
    // #1500) opens its visualization (the dynamic impact map); the rest open
    // their overlay locally.
    let open = route ? () => goTo(route) : () => setOpenTileKey(key);
    const moduleSummary = data?.moduleSummary;
    const liveRoute = moduleSummary?.liveModuleId
      && moduleSummary.summaries?.find((s) => s.moduleId === moduleSummary.liveModuleId)?.openRoute;
    if (key === 'progress' && liveRoute) {
      open = () => goTo(liveRoute);
    }
    return (
      <TileFrame
        key={key}
        style={style}
        label={t(labelKey)}
        scrollBody={key === 'flow'}
        onOpen={open}
      >
        <Tile t={t} dataKey={dataKey} />
      </TileFrame>
    );
  };

  return (
    <Page>
      {/* The standard module header (decision 2026-10-19): rainbow title with
          the view dropdown, the Kuutio's name underneath. */}
      <MainViewHeader
        activeView="dashboard"
        instanceTitle={currentInstance?.title || ''}
        inDocumentFlow
      >
        <StatusChip stale={stale}>
          {stale ? t('dashboard.stale') : t('dashboard.live')}
        </StatusChip>
        <Clock>{clock}</Clock>
      </MainViewHeader>
      <Content>
        <LeftBar>
          {/* The schedule tile is hidden for now — the flow bar's timer covers
              the corner (adjustment 2026-10-19); see tileRegistry.jsx. */}
          {renderTile('flow', FLOW_STYLE)}
        </LeftBar>
        <Main>
          {renderTile('progress', PROGRESS_STYLE)}
          <BottomRow>
            {BOTTOM_ROW_KEYS.map((key) => renderTile(key))}
          </BottomRow>
        </Main>
      </Content>
      {openTile?.Detail && (
        <DetailOverlay
          title={t(openTile.labelKey)}
          closeLabel={t('dashboard.close')}
          onClose={() => setOpenTileKey(null)}
        >
          <openTile.Detail t={t} dataKey={openTile.dataKey} />
        </DetailOverlay>
      )}
    </Page>
  );
}

export default function DashboardScreen() {
  useScreenNavigation();
  useScreenStateUpdates('dashboard', { tiles: DASHBOARD_TILES.length });
  const instanceId = useInstanceId();
  const { t } = useTranslation();

  if (!instanceId) {
    return (
      <Page>
        <EmptyState>{t('dashboard.noInstance')}</EmptyState>
      </Page>
    );
  }

  return (
    <DashboardDataProvider instanceId={instanceId}>
      <DashboardBody />
    </DashboardDataProvider>
  );
}
